import { useState, useEffect } from 'react';
import { Save, X, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SpecificationTemplate, DeliveryLocationTemplate, JobCategory, SpecificationType, LocationType, JOB_CATEGORIES } from '@/lib/types/job';
import { toast } from '@/hooks/use-toast';
import { useAddressValidation, StandardizedAddress } from '@/hooks/useAddressValidation';
import { AddressValidationBadge } from '@/components/shared/AddressValidationBadge';
import { AddressSuggestionDialog } from '@/components/shared/AddressSuggestionDialog';
import { addressDiffersFromStandardized } from '@/utils/addressUtils';

interface TemplateEditorProps {
  template?: SpecificationTemplate | DeliveryLocationTemplate;
  type?: 'specification' | 'delivery';
  onSave: (template: any, type: 'specification' | 'delivery') => void;
  onCancel?: () => void;
}

export function TemplateEditor({ template, type: initialType, onSave, onCancel }: TemplateEditorProps) {
  const [type, setType] = useState<'specification' | 'delivery'>(
    initialType || (template && 'specification_name' in template ? 'specification' : 'delivery')
  );
  
  // Specification template state
  const [specData, setSpecData] = useState({
    template_name: '',
    template_description: '',
    category: '' as JobCategory,
    specification_type: '' as SpecificationType,
    content: {},
    sections: [] as any[],
    required_fields: [] as string[],
    tags: [] as string[],
  });

  // Delivery template state
  const [deliveryData, setDeliveryData] = useState({
    location_name: '',
    location_type: '' as LocationType,
    address_line1: '',
    address_line2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'United States',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    special_instructions: '',
    access_requirements: '',
    delivery_hours: {},
    preferred_carriers: [] as string[],
    restricted_carriers: [] as string[],
    dock_information: '',
  });

  // Common state
  const [newTag, setNewTag] = useState('');
  const [newSection, setNewSection] = useState({ title: '', content: '', required: false });
  const [newCarrier, setNewCarrier] = useState('');

  // Address validation state
  const { validateAddress, isValidating, lastResult, clearValidation } = useAddressValidation();
  const [addressValidated, setAddressValidated] = useState(false);
  const [addressDeliverability, setAddressDeliverability] = useState<string | undefined>(undefined);
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Clear validation when address fields change
  useEffect(() => {
    if (addressValidated) {
      setAddressValidated(false);
      setAddressDeliverability(undefined);
      clearValidation();
    }
  }, [deliveryData.address_line1, deliveryData.city, deliveryData.state_province, deliveryData.postal_code]);

  useEffect(() => {
    if (template) {
      if ('specification_name' in template) {
        setSpecData({
          template_name: template.specification_name || '',
          template_description: template.specification_description || '',
          category: template.category,
          specification_type: template.specification_type,
          content: template.content || {},
          sections: template.sections || [],
          required_fields: template.required_fields || [],
          tags: template.tags || [],
        });
        setType('specification');
      } else {
        setDeliveryData({
          location_name: template.location_name || '',
          location_type: template.location_type,
          address_line1: template.address_line1 || '',
          address_line2: template.address_line2 || '',
          city: template.city || '',
          state_province: template.state_province || '',
          postal_code: template.postal_code || '',
          country: template.country || 'United States',
          contact_name: template.contact_name || '',
          contact_phone: template.contact_phone || '',
          contact_email: template.contact_email || '',
          special_instructions: template.special_instructions || '',
          access_requirements: template.access_requirements || '',
          delivery_hours: template.delivery_hours || {},
          preferred_carriers: template.preferred_carriers || [],
          restricted_carriers: template.restricted_carriers || [],
          dock_information: template.dock_information || '',
        });
        setType('delivery');
      }
    }
  }, [template]);

  const specificationTypes: { value: SpecificationType; label: string }[] = [
    { value: 'my_organization', label: 'My Organization' },
    { value: 'personal', label: 'Personal' },
  ];

  const locationTypes: { value: LocationType; label: string }[] = [
    { value: 'office', label: 'Office' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'job_site', label: 'Job Site' },
    { value: 'distribution_center', label: 'Distribution Center' },
    { value: 'retail_location', label: 'Retail Location' },
    { value: 'other', label: 'Other' },
  ];

  const handleSave = async () => {
    // Validate delivery template
    if (type === 'delivery') {
      if (!deliveryData.location_name.trim()) {
        toast({
          title: "Location Name Required",
          description: "Please enter a location name for this delivery template.",
          variant: "destructive",
        });
        return;
      }

      if (!deliveryData.location_type || (deliveryData.location_type as string) === '') {
        toast({
          title: "Location Type Required",
          description: "Please select a location type (Office, Warehouse, etc.).",
          variant: "destructive",
        });
        return;
      }

      if (!deliveryData.address_line1.trim()) {
        toast({
          title: "Address Required",
          description: "Please enter a street address.",
          variant: "destructive",
        });
        return;
      }

      if (!deliveryData.city.trim()) {
        toast({
          title: "City Required",
          description: "Please enter a city.",
          variant: "destructive",
        });
        return;
      }

      if (!deliveryData.state_province.trim()) {
        toast({
          title: "State/Province Required",
          description: "Please enter a state or province.",
          variant: "destructive",
        });
        return;
      }

      if (!deliveryData.postal_code.trim()) {
        toast({
          title: "Postal Code Required",
          description: "Please enter a postal code.",
          variant: "destructive",
        });
        return;
      }

      // Perform address validation via Smarty API
      setIsSaving(true);
      const result = await validateAddress({
        street: deliveryData.address_line1,
        city: deliveryData.city,
        state: deliveryData.state_province,
        zipcode: deliveryData.postal_code,
      });

      // If valid and address differs from standardized, show suggestion dialog
      if (result.isValid && result.standardizedAddress) {
        const differs = addressDiffersFromStandardized(
          {
            street: deliveryData.address_line1,
            city: deliveryData.city,
            state: deliveryData.state_province,
            zipcode: deliveryData.postal_code,
          },
          result.standardizedAddress
        );

        if (differs) {
          setShowSuggestionDialog(true);
          setIsSaving(false);
          return;
        }

        // Address matches standardized, save with validated status
        saveDeliveryTemplate(true, result.deliverability);
        return;
      }

      // If invalid but has suggestions, show dialog
      if (!result.isValid && result.suggestions && result.suggestions.length > 0) {
        setShowSuggestionDialog(true);
        setIsSaving(false);
        return;
      }

      // If truly invalid with no suggestions, allow saving with warning
      if (!result.isValid) {
        setIsSaving(false);
        setShowSuggestionDialog(true);
        return;
      }

      // Valid address, save it
      saveDeliveryTemplate(true, result.deliverability);
      return;
    }

    // Validate specification template
    if (type === 'specification') {
      if (!specData.template_name.trim()) {
        toast({
          title: "Template Name Required",
          description: "Please enter a name for this specification template.",
          variant: "destructive",
        });
        return;
      }

      if (!specData.category) {
        toast({
          title: "Category Required",
          description: "Please select a category.",
          variant: "destructive",
        });
        return;
      }

      if (!specData.specification_type) {
        toast({
          title: "Specification Type Required",
          description: "Please select a specification type.",
          variant: "destructive",
        });
        return;
      }

      onSave(specData, type);
    }
  };

  // Save delivery template with validation data
  const saveDeliveryTemplate = (
    validated: boolean,
    deliverability?: string,
    overrideData?: Partial<typeof deliveryData>
  ) => {
    const dataToSave = {
      ...deliveryData,
      ...overrideData,
      address_validated: validated,
      address_validated_at: validated ? new Date().toISOString() : null,
      address_deliverability: deliverability || null,
    };

    onSave(dataToSave, type);
    setIsSaving(false);
  };

  // When user selects a suggested address
  const handleSelectSuggestedAddress = (suggestedAddress: StandardizedAddress) => {
    const updatedAddressFields = {
      address_line1: suggestedAddress.street,
      address_line2: suggestedAddress.street2 || '',
      city: suggestedAddress.city,
      state_province: suggestedAddress.state,
      postal_code: suggestedAddress.plus4
        ? `${suggestedAddress.zipcode}-${suggestedAddress.plus4}`
        : suggestedAddress.zipcode,
    };

    // Update state for UI consistency
    setDeliveryData(prev => ({
      ...prev,
      ...updatedAddressFields,
    }));
    setAddressValidated(true);
    setAddressDeliverability(lastResult?.deliverability);

    // Pass the updated fields directly to save to avoid stale state
    saveDeliveryTemplate(true, lastResult?.deliverability, updatedAddressFields);
  };

  // When user keeps original (unverified) address
  const handleKeepOriginal = () => {
    saveDeliveryTemplate(false, 'unknown');
  };

  // When user wants to edit the address
  const handleEditAddress = () => {
    setShowSuggestionDialog(false);
  };

  // Manual validation button handler
  const handleManualValidation = async () => {
    const result = await validateAddress({
      street: deliveryData.address_line1,
      city: deliveryData.city,
      state: deliveryData.state_province,
      zipcode: deliveryData.postal_code,
    });

    if (result.isValid) {
      setAddressValidated(true);
      setAddressDeliverability(result.deliverability);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !specData.tags.includes(newTag.trim())) {
      setSpecData({
        ...specData,
        tags: [...specData.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setSpecData({
      ...specData,
      tags: specData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const addSection = () => {
    if (newSection.title.trim()) {
      setSpecData({
        ...specData,
        sections: [...specData.sections, { ...newSection, id: Date.now().toString() }]
      });
      setNewSection({ title: '', content: '', required: false });
    }
  };

  const removeSection = (sectionId: string) => {
    setSpecData({
      ...specData,
      sections: specData.sections.filter(section => section.id !== sectionId)
    });
  };

  const addCarrier = (carrierType: 'preferred' | 'restricted') => {
    if (newCarrier.trim()) {
      const carriers = carrierType === 'preferred' ? deliveryData.preferred_carriers : deliveryData.restricted_carriers;
      if (!carriers.includes(newCarrier.trim())) {
        setDeliveryData({
          ...deliveryData,
          [carrierType === 'preferred' ? 'preferred_carriers' : 'restricted_carriers']: [
            ...carriers,
            newCarrier.trim()
          ]
        });
        setNewCarrier('');
      }
    }
  };

  const removeCarrier = (carrier: string, carrierType: 'preferred' | 'restricted') => {
    const field = carrierType === 'preferred' ? 'preferred_carriers' : 'restricted_carriers';
    setDeliveryData({
      ...deliveryData,
      [field]: deliveryData[field].filter(c => c !== carrier)
    });
  };

  return (
    <div className="space-y-6">
      {!template && !initialType && (
        <Tabs value={type} onValueChange={(value) => setType(value as 'specification' | 'delivery')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="specification">Specification Template</TabsTrigger>
            <TabsTrigger value="delivery">Delivery Template</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {type === 'specification' ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="template_name">Template Name *</Label>
              <Input
                id="template_name"
                value={specData.template_name}
                onChange={(e) => setSpecData({ ...specData, template_name: e.target.value })}
                placeholder="e.g., Standard Office Supplies Specification"
              />
            </div>
            <div>
              <Label htmlFor="category">Category *</Label>
              <Select value={specData.category} onValueChange={(value) => setSpecData({ ...specData, category: value as JobCategory })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {JOB_CATEGORIES.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="template_description">Description</Label>
            <Textarea
              id="template_description"
              value={specData.template_description}
              onChange={(e) => setSpecData({ ...specData, template_description: e.target.value })}
              placeholder="Brief description of when to use this template..."
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="specification_type">Specification Type *</Label>
            <Select value={specData.specification_type} onValueChange={(value) => setSpecData({ ...specData, specification_type: value as SpecificationType })}>
              <SelectTrigger>
                <SelectValue placeholder="Select specification type" />
              </SelectTrigger>
              <SelectContent>
                {specificationTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tags Section */}
          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag..."
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  />
                  <Button onClick={addTag}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {specData.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(tag)} />
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sections */}
          <Card>
            <CardHeader>
              <CardTitle>Specification Sections</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {specData.sections.map((section) => (
                  <div key={section.id} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium">{section.title}</h4>
                      <Button size="sm" variant="ghost" onClick={() => removeSection(section.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">{section.content}</p>
                    {section.required && <Badge variant="secondary" className="mt-2">Required</Badge>}
                  </div>
                ))}

                <div className="p-4 border-2 border-dashed rounded-lg space-y-3">
                  <h4 className="font-medium">Add New Section</h4>
                  <Input
                    value={newSection.title}
                    onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
                    placeholder="Section title..."
                  />
                  <Textarea
                    value={newSection.content}
                    onChange={(e) => setNewSection({ ...newSection, content: e.target.value })}
                    placeholder="Section content..."
                    rows={3}
                  />
                  <div className="flex justify-between">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newSection.required}
                        onChange={(e) => setNewSection({ ...newSection, required: e.target.checked })}
                      />
                      Required section
                    </label>
                    <Button onClick={addSection}>Add Section</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="location_name">Location Name *</Label>
              <Input
                id="location_name"
                value={deliveryData.location_name}
                onChange={(e) => setDeliveryData({ ...deliveryData, location_name: e.target.value })}
                placeholder="e.g., Main Office, Warehouse A"
              />
            </div>
            <div>
              <Label htmlFor="location_type">Location Type *</Label>
              <Select value={deliveryData.location_type} onValueChange={(value) => setDeliveryData({ ...deliveryData, location_type: value as LocationType })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location type" />
                </SelectTrigger>
                <SelectContent>
                  {locationTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Address */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Address</CardTitle>
                <AddressValidationBadge
                  isValidated={addressValidated}
                  deliverability={addressDeliverability as any}
                  onVerify={handleManualValidation}
                  isVerifying={isValidating}
                  compact
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Address Line 1 *</Label>
                <Input
                  value={deliveryData.address_line1}
                  onChange={(e) => setDeliveryData({ ...deliveryData, address_line1: e.target.value })}
                  placeholder="Street address"
                />
              </div>
              <div>
                <Label>Address Line 2</Label>
                <Input
                  value={deliveryData.address_line2}
                  onChange={(e) => setDeliveryData({ ...deliveryData, address_line2: e.target.value })}
                  placeholder="Apt, suite, floor, etc."
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>City *</Label>
                  <Input
                    value={deliveryData.city}
                    onChange={(e) => setDeliveryData({ ...deliveryData, city: e.target.value })}
                  />
                </div>
                <div>
                  <Label>State/Province *</Label>
                  <Select
                    value={deliveryData.state_province}
                    onValueChange={(v) => setDeliveryData({ ...deliveryData, state_province: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="AL">Alabama (AL)</SelectItem>
                      <SelectItem value="AK">Alaska (AK)</SelectItem>
                      <SelectItem value="AZ">Arizona (AZ)</SelectItem>
                      <SelectItem value="AR">Arkansas (AR)</SelectItem>
                      <SelectItem value="CA">California (CA)</SelectItem>
                      <SelectItem value="CO">Colorado (CO)</SelectItem>
                      <SelectItem value="CT">Connecticut (CT)</SelectItem>
                      <SelectItem value="DE">Delaware (DE)</SelectItem>
                      <SelectItem value="FL">Florida (FL)</SelectItem>
                      <SelectItem value="GA">Georgia (GA)</SelectItem>
                      <SelectItem value="HI">Hawaii (HI)</SelectItem>
                      <SelectItem value="ID">Idaho (ID)</SelectItem>
                      <SelectItem value="IL">Illinois (IL)</SelectItem>
                      <SelectItem value="IN">Indiana (IN)</SelectItem>
                      <SelectItem value="IA">Iowa (IA)</SelectItem>
                      <SelectItem value="KS">Kansas (KS)</SelectItem>
                      <SelectItem value="KY">Kentucky (KY)</SelectItem>
                      <SelectItem value="LA">Louisiana (LA)</SelectItem>
                      <SelectItem value="ME">Maine (ME)</SelectItem>
                      <SelectItem value="MD">Maryland (MD)</SelectItem>
                      <SelectItem value="MA">Massachusetts (MA)</SelectItem>
                      <SelectItem value="MI">Michigan (MI)</SelectItem>
                      <SelectItem value="MN">Minnesota (MN)</SelectItem>
                      <SelectItem value="MS">Mississippi (MS)</SelectItem>
                      <SelectItem value="MO">Missouri (MO)</SelectItem>
                      <SelectItem value="MT">Montana (MT)</SelectItem>
                      <SelectItem value="NE">Nebraska (NE)</SelectItem>
                      <SelectItem value="NV">Nevada (NV)</SelectItem>
                      <SelectItem value="NH">New Hampshire (NH)</SelectItem>
                      <SelectItem value="NJ">New Jersey (NJ)</SelectItem>
                      <SelectItem value="NM">New Mexico (NM)</SelectItem>
                      <SelectItem value="NY">New York (NY)</SelectItem>
                      <SelectItem value="NC">North Carolina (NC)</SelectItem>
                      <SelectItem value="ND">North Dakota (ND)</SelectItem>
                      <SelectItem value="OH">Ohio (OH)</SelectItem>
                      <SelectItem value="OK">Oklahoma (OK)</SelectItem>
                      <SelectItem value="OR">Oregon (OR)</SelectItem>
                      <SelectItem value="PA">Pennsylvania (PA)</SelectItem>
                      <SelectItem value="RI">Rhode Island (RI)</SelectItem>
                      <SelectItem value="SC">South Carolina (SC)</SelectItem>
                      <SelectItem value="SD">South Dakota (SD)</SelectItem>
                      <SelectItem value="TN">Tennessee (TN)</SelectItem>
                      <SelectItem value="TX">Texas (TX)</SelectItem>
                      <SelectItem value="UT">Utah (UT)</SelectItem>
                      <SelectItem value="VT">Vermont (VT)</SelectItem>
                      <SelectItem value="VA">Virginia (VA)</SelectItem>
                      <SelectItem value="WA">Washington (WA)</SelectItem>
                      <SelectItem value="WV">West Virginia (WV)</SelectItem>
                      <SelectItem value="WI">Wisconsin (WI)</SelectItem>
                      <SelectItem value="WY">Wyoming (WY)</SelectItem>
                      <SelectItem value="DC">District of Columbia (DC)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Postal Code *</Label>
                  <Input
                    value={deliveryData.postal_code}
                    onChange={(e) => setDeliveryData({ ...deliveryData, postal_code: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={deliveryData.contact_name}
                  onChange={(e) => setDeliveryData({ ...deliveryData, contact_name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={deliveryData.contact_phone}
                    onChange={(e) => setDeliveryData({ ...deliveryData, contact_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={deliveryData.contact_email}
                    onChange={(e) => setDeliveryData({ ...deliveryData, contact_email: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Delivery Instructions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Special Instructions</Label>
                <Textarea
                  value={deliveryData.special_instructions}
                  onChange={(e) => setDeliveryData({ ...deliveryData, special_instructions: e.target.value })}
                  placeholder="Any special delivery instructions..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Access Requirements</Label>
                <Textarea
                  value={deliveryData.access_requirements}
                  onChange={(e) => setDeliveryData({ ...deliveryData, access_requirements: e.target.value })}
                  placeholder="Security badges, escort requirements, etc."
                  rows={2}
                />
              </div>
              <div>
                <Label>Dock Information</Label>
                <Textarea
                  value={deliveryData.dock_information}
                  onChange={(e) => setDeliveryData({ ...deliveryData, dock_information: e.target.value })}
                  placeholder="Dock numbers, loading specifications, etc."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Carriers */}
          <Card>
            <CardHeader>
              <CardTitle>Carrier Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Preferred Carriers</Label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={newCarrier}
                      onChange={(e) => setNewCarrier(e.target.value)}
                      placeholder="Add carrier..."
                    />
                    <Button onClick={() => addCarrier('preferred')}>Add</Button>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {deliveryData.preferred_carriers.map((carrier) => (
                      <Badge key={carrier} variant="secondary" className="flex items-center gap-1">
                        {carrier}
                        <X className="h-3 w-3 cursor-pointer" onClick={() => removeCarrier(carrier, 'preferred')} />
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <Label>Restricted Carriers</Label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={newCarrier}
                      onChange={(e) => setNewCarrier(e.target.value)}
                      placeholder="Add restricted carrier..."
                    />
                    <Button onClick={() => addCarrier('restricted')}>Add</Button>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {deliveryData.restricted_carriers.map((carrier) => (
                      <Badge key={carrier} variant="destructive" className="flex items-center gap-1">
                        {carrier}
                        <X className="h-3 w-3 cursor-pointer" onClick={() => removeCarrier(carrier, 'restricted')} />
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button onClick={handleSave} disabled={isValidating || isSaving}>
          {(isValidating || isSaving) ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {isValidating ? 'Validating...' : isSaving ? 'Saving...' : 'Save Template'}
        </Button>
      </div>

      {/* Address Suggestion Dialog */}
      {type === 'delivery' && (
        <AddressSuggestionDialog
          open={showSuggestionDialog}
          onOpenChange={setShowSuggestionDialog}
          originalAddress={{
            street: deliveryData.address_line1,
            city: deliveryData.city,
            state: deliveryData.state_province,
            zipcode: deliveryData.postal_code,
          }}
          validationResult={lastResult || { isValid: false, deliverability: 'unknown' }}
          onSelectAddress={handleSelectSuggestedAddress}
          onKeepOriginal={handleKeepOriginal}
          onEditAddress={handleEditAddress}
        />
      )}
    </div>
  );
}